import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { checkIsInternal, checkIsSimulationMode } from '@src/utility/createStorage';
import { fetchSiteNotifications } from '@src/middleware/services/notificationBanner/NotificationBanner.service';

dayjs.extend(utc);
dayjs.extend(timezone);

type SiteNotification = {
  id: number | string;
  bannerType: string;
  loginType: string;
  journeyIds: number[];
  message: string;
  startDate: string;
  endDate: string;
};

const toNumbers = (value: unknown): number[] => {
  if (Array.isArray(value)) return value.map(Number).filter(Number.isFinite);
  return String(value || '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter(Number.isFinite);
};

const normalize = (item: Record<string, unknown>): SiteNotification => ({
  id: (item.Id ?? item.id) as number | string,
  bannerType: String(item.BannerType ?? item.bannerType ?? ''),
  loginType: String(item.Sites ?? item.LoginType ?? item.loginType ?? ''),
  journeyIds: toNumbers(item.Category ?? item.categories),
  message: String(item.Message ?? item.description ?? item.message ?? ''),
  startDate: String(item.StartDate ?? item.startDate ?? ''),
  endDate: String(item.EndDate ?? item.endDate ?? ''),
});

const parseNotificationTime = (value: string) => {
  const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return (hasTimeZone ? dayjs(value) : dayjs.tz(value, 'America/Chicago')).valueOf();
};

export const activeNotifications = (
  records: Record<string, unknown>[],
  journeyIds: number[],
  accessType: 'Internal' | 'External'
): SiteNotification[] => {
  const now = dayjs().tz('America/Chicago').valueOf();
  return records.map(normalize).filter((item) => {
    const start = parseNotificationTime(item.startDate);
    const end = parseNotificationTime(item.endDate);
    // Empty Category means the admin didn't restrict the banner to specific pages — show it everywhere.
    // Page-scoped notifications require an active matching journey.
    const matchesJourney =
      item.journeyIds.length === 0 ||
      (journeyIds.length > 0 && item.journeyIds.some((id) => journeyIds.includes(id)));

    return (
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      start <= now &&
      end >= now &&
      matchesJourney &&
      (item.loginType === 'Both' || item.loginType === accessType)
    );
  });
};

const getJourneyIds = (): number[] => {
  const value = sessionStorage.getItem('journeyId');
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return toNumbers(parsed);
  } catch {
    // journeyId may be stored as a comma-separated value.
  }
  return toNumbers(value);
};

const cookieKey = (item: SiteNotification) => item.journeyIds.join('_');

const hasCookie = (item: SiteNotification) =>
  document.cookie.split('; ').some((entry) => entry.startsWith(`${cookieKey(item)}=`));

// Don't show banners/pop-ups on the admin page used to author them.
const isNotificationManagerRoute = () =>
  window.location.pathname.includes('/content-editor/notification-banner-manager');

type NotificationBannerProps = {
  item: SiteNotification;
  onClose: (id: SiteNotification['id']) => void;
};

const NotificationBanner: React.FC<NotificationBannerProps> = ({ item, onClose }) => (
  <div
    style={{
      alignItems: 'center',
      backgroundColor: item.bannerType === 'Warning' ? '#fffcd9' : '#e3f0fd',
      border: item.bannerType === 'Warning' ? '1px solid #ffd00d' : '1px solid #196ecf',
      boxSizing: 'border-box',
      color: '#2d2d39',
      display: 'flex',
      justifyContent: 'space-between',
      minHeight: '42px',
      padding: '6px 12px 6px 30px',
      width: '100%',
    }}
  >
    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.message) }} />
    <IconButton aria-label="Close notification" size="small" onClick={() => onClose(item.id)}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </div>
);

export const SiteNotifications: React.FC = () => {
  const { pathname } = useLocation();
  const [notifications, setNotifications] = useState<SiteNotification[]>([]);
  const [popup, setPopup] = useState<SiteNotification | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (isNotificationManagerRoute()) {
        setNotifications([]);
        setPopup(null);
        return;
      }
      try {
        const records = await fetchSiteNotifications();
        if (!mounted) return;
        const accessType = checkIsInternal() && !checkIsSimulationMode() ? 'Internal' : 'External';
        const active = activeNotifications(records || [], getJourneyIds(), accessType);
        setNotifications(active);
        setPopup(active.find((item) => item.bannerType === 'Pop-Up' && !hasCookie(item)) || null);
      } catch {
        if (mounted) {
          setNotifications([]);
          setPopup(null);
        }
      }
    };

    // AppShell updates journeyId before remounting this component for a new pathname.
    const initialCheck = window.setTimeout(load, 0);
    return () => {
      mounted = false;
      window.clearTimeout(initialCheck);
    };
  }, [pathname]);

  const closeBanner = (id: SiteNotification['id']) => {
    setNotifications((current) => current.filter((entry) => entry.id !== id));
  };

  const closePopup = () => {
    if (!popup) return;
    const expires = new Date();
    expires.setDate(expires.getDate() + 1);
    document.cookie = `${cookieKey(popup)}=dont%20pop%20up; expires=${expires.toUTCString()}; path=/`;
    setPopup(null);
  };

  return (
    <>
      {notifications.map((item) =>
        item.bannerType === 'Pop-Up' ? null : <NotificationBanner key={item.id} item={item} onClose={closeBanner} />
      )}
      <Dialog open={Boolean(popup)} onClose={closePopup} aria-label="Site notification">
        <DialogContent sx={{ minWidth: { xs: '240px', sm: '454px' }, minHeight: '160px', position: 'relative' }}>
          <IconButton aria-label="Close notification" onClick={closePopup} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
          {popup && (
            <>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(popup.message) }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SiteNotifications;
